import { useQuery } from "@tanstack/react-query";
import { TrendUp, TrendDown, Minus } from "@phosphor-icons/react";
import { useApi, money, fmtDate } from "@/lib/api";

const months = (m) => `${Math.abs(m).toFixed(1)} months`;
const driverText = (d) => {
  const up = d.change > 0;
  const verb = d.key === "burn" ? (up ? "burn rose" : "burn fell") : d.key === "mrr" ? (up ? "MRR grew" : "MRR dropped") : (up ? "cash rose" : "cash fell");
  return `${verb} ${money(Math.abs(d.change), true)}${d.key === "cash" ? "" : "/mo"}`;
};

const headline = (s) => {
  if (s.days_moved == null) return s.zero_cash_date ? "Your Zero Cash Date is new since the last digest" : "Cash no longer runs out within the forecast window";
  if (s.days_moved === 0) return "Runway held steady since the last digest";
  const longer = s.days_moved > 0;
  const m = Math.abs(s.days_moved) / 30.44;
  const span = m >= 1 ? months(m) : `${Math.abs(s.days_moved)} day${Math.abs(s.days_moved) === 1 ? "" : "s"}`;
  return `Runway is ${span} ${longer ? "longer" : "shorter"} than at the last digest`;
};

export const ChangeSummary = () => {
  const api = useApi();
  const { data: s } = useQuery({ queryKey: ["forecast-change-summary"], queryFn: () => api.get("/forecast/change-summary") });
  if (!s) return null;
  if (!s.available) {
    return <p data-testid="forecast-change-summary" className="mt-4 text-xs text-[#6B6B6B]">Your first week-over-week runway note appears after next Monday's digest.</p>;
  }
  const dir = s.days_moved == null || s.days_moved === 0 ? 0 : s.days_moved > 0 ? 1 : -1;
  const Icon = dir > 0 ? TrendUp : dir < 0 ? TrendDown : Minus;
  const color = dir > 0 ? "text-[#00FF9D]" : dir < 0 ? "text-[#FF8FA3]" : "text-[#A3A3A3]";
  const why = s.drivers.map(driverText);
  return (
    <div data-testid="forecast-change-summary" className="mt-4 flex items-start gap-3 rounded-lg border border-[#2A2A2A] bg-[#0D0D0D] px-4 py-3 text-sm">
      <Icon size={18} weight="bold" className={`${color} mt-0.5 shrink-0`} />
      <p className="text-[#CFCFCF] leading-relaxed">
        <span data-testid="forecast-change-headline" className={`font-medium ${color}`}>{headline(s)}</span>
        {s.days_moved != null && s.days_moved !== 0 && (
          <> (Zero Cash Date {fmtDate(s.previous_zero_cash_date)} → <span className="font-mono">{fmtDate(s.zero_cash_date)}</span>)</>
        )}
        {why.length > 0 && <span data-testid="forecast-change-drivers">, mainly because {why.join(" and ")}</span>}.
        <span className="ml-1 text-[11px] text-[#6B6B6B]">Compared with the {fmtDate(s.since)} digest.</span>
      </p>
    </div>
  );
};
